// comestibleValidacion.js — P3-5b (remate). Dos cosas:
//  (1) validarComestibleBasico: chequeos en el FORMULARIO antes de llamar al motor
//      (nombre, al menos un sabor y un pack, precio por variación) con mensajes en
//      español y un ancla al campo — para no round-trippear por errores obvios.
//  (2) traducirErroresMotor: el motor devuelve { campo: RUTA del schema, mensaje: texto
//      (a veces INGLÉS de ajv) }. Acá se mapea la ruta a un nombre de campo en español
//      + un mensaje en español + un ancla, SIN mostrar nunca la ruta técnica ni inglés.
// Frontend-only → export ES. Las anclas son ids que SeccionComestible pone en sus bloques.
import { claveVariacion } from './comestibleDraft';

const vacio = (s) => !String(s == null ? '' : s).trim();

// ── (1) Validación básica del formulario ────────────────────────────────────
export function validarComestibleBasico(form = {}, c = {}) {
  const errores = [];
  if (vacio(form.name)) errores.push({ campo: 'Nombre del producto', mensaje: 'Falta el nombre.', ancla: null });
  const sabores = (c.sabores || []).filter((s) => !vacio(s.nombre));
  const packs = (c.packs || []).filter((p) => !vacio(p.nombre));
  if (!sabores.length) errores.push({ campo: 'Sabores', mensaje: 'Agrega al menos un sabor.', ancla: 'com-sabores' });
  if (!packs.length) errores.push({ campo: 'Presentaciones', mensaje: 'Agrega al menos un pack.', ancla: 'com-packs' });
  // Cada combinación sabor × pack necesita precio (el motor lo exige igual).
  for (const s of sabores) {
    for (const p of packs) {
      const v = (c.variaciones || {})[claveVariacion(s.nombre, p.nombre)] || {};
      if (vacio(v.precio)) errores.push({ campo: `Variación «${s.nombre} · ${p.nombre}»`, mensaje: 'Falta el precio.', ancla: 'com-variaciones' });
    }
  }
  return errores;
}

// ── (2) Traducción de los errores del motor ─────────────────────────────────
function traducirMensajeAjv(mensaje) {
  const m = String(mensaje || '');
  if (/no coincide con ninguna meta|clave desconocida/i.test(m)) return 'Hay un valor que el contrato no reconoce.';
  if (/required/i.test(m)) return 'Falta completar este campo.';
  const tope = m.match(/more than (\d+) items/i);
  if (tope) return `Hay demasiados; el máximo es ${tope[1]}.`;
  if (/format|uri/i.test(m)) return 'No parece una dirección web válida.';
  if (/must be|should be|must NOT|invalid/i.test(m)) return 'El formato de este dato no es correcto.';
  return 'Revisa este dato.';
}

export function traducirErrorMotor({ campo = '', mensaje = '' } = {}) {
  const ruta = String(campo);
  const fila = (re) => { const x = ruta.match(re); return x ? Number(x[1]) + 1 : null; };

  // Tabla nutricional.
  let n = fila(/filas\[(\d+)\]\.vd/);
  if (n) return { campo: `Tabla nutricional · fila ${n} · %VD`, mensaje: 'El %VD va solo con el número, sin «%».', ancla: 'com-nutricion' };
  n = fila(/filas\[(\d+)\]\.(?:nutriente|cantidad|unidad)/);
  if (n) return { campo: `Tabla nutricional · fila ${n}`, mensaje: 'Falta un dato de la fila.', ancla: 'com-nutricion' };

  // Beneficios (HTML acotado) — la regla de Neto lo dice en el mensaje.
  if (/HTML solo admite|beneficios_detalle/i.test(`${ruta} ${mensaje}`)) {
    return { campo: 'Beneficios', mensaje: 'El texto de beneficios solo admite negritas, cursiva y listas simples.', ancla: 'com-beneficios' };
  }

  // Variaciones.
  n = fila(/^variaciones\[(\d+)\]\.regular_price/);
  if (n) return { campo: `Variación ${n} · precio`, mensaje: 'Falta el precio.', ancla: 'com-variaciones' };
  n = fila(/^variaciones\[(\d+)\]/);
  if (n) return { campo: `Variación ${n}`, mensaje: traducirMensajeAjv(mensaje), ancla: 'com-variaciones' };

  // Campos del producto base.
  if (/^producto\.name/.test(ruta)) return { campo: 'Nombre del producto', mensaje: 'Falta el nombre.', ancla: null };
  if (/^producto\.cross_sell_ids/.test(ruta)) return { campo: 'Productos relacionados', mensaje: 'Máximo 3 productos relacionados.', ancla: null };
  if (/^producto\.images/.test(ruta)) return { campo: 'Imágenes', mensaje: traducirMensajeAjv(mensaje), ancla: null };
  if (/_vitrina_subtitulo|subtitulo/i.test(ruta)) return { campo: 'Subtítulo', mensaje: traducirMensajeAjv(mensaje), ancla: 'com-subtitulo' };

  // Genérico: NUNCA la ruta ni inglés.
  return { campo: 'Comestible', mensaje: traducirMensajeAjv(mensaje), ancla: null };
}

export function traducirErroresMotor(errores = []) {
  return (errores || []).map(traducirErrorMotor);
}
